import { useState, useCallback, useEffect, useRef } from 'react'
import { Copy, Check, FileText, RefreshCw, ThumbsUp, ThumbsDown, ImageIcon, Download } from 'lucide-react'
import { Markdown, Spinner, Tooltip, TooltipTrigger, TooltipContent, cn } from '@neo-agents/ui'
import { ImagePreview } from './ImagePreview'
import type { ChatMessage } from '../state/types'
import { AttachmentImage } from './AttachmentImage'
import { FilePreview } from './FilePreview'
import { getNextTypedLength } from './typewriter'
import { RobotLogo } from './RobotLogo'

interface ChatMessageBubbleProps {
  message: ChatMessage
  isLast?: boolean
  onRegenerate?: () => void
  onVote?: (messageId: string, vote: 'up' | 'down' | null) => void
}

function BotAvatar() {
  return (
    <div className="mt-0.5 shrink-0">
      <RobotLogo className="h-8 w-8 nb-shadow-sm" />
    </div>
  )
}

function formatTime(ts: number): string {
  const d = new Date(ts)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function StreamingLabel({ label }: { label: string }) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(Date.now())

  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Date.now() - startRef.current)
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const seconds = Math.floor(elapsed / 1000)
  const timeStr = seconds > 0 ? ` ${seconds}s` : ''

  return (
    <span className="inline-flex items-center gap-2 text-foreground/50 text-sm font-base">
      <Spinner className="text-xs" />
      <span>{label}{timeStr}</span>
    </span>
  )
}

export function ChatMessageBubble({ message, isLast, onRegenerate, onVote }: ChatMessageBubbleProps) {
  const [copied, setCopied] = useState(false)
  const [viewMode, setViewMode] = useState<'rendered' | 'markdown'>('rendered')
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const [filePreview, setFilePreview] = useState<{ src: string; name: string; mimeType: string } | null>(null)
  const isError = message.content.startsWith('Error:')

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(message.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [message.content])

  const handleVoteUp = useCallback(() => {
    if (onVote) {
      onVote(message.id, message.vote === 'up' ? null : 'up')
    }
  }, [onVote, message.id, message.vote])

  const handleVoteDown = useCallback(() => {
    if (onVote) {
      onVote(message.id, message.vote === 'down' ? null : 'down')
    }
  }, [onVote, message.id, message.vote])

  const hasImage = !!message.imageUrl
  const imageAttachments = message.attachments?.filter((attachment) => attachment.type.startsWith('image/')) ?? []
  const fileAttachments = message.attachments?.filter((attachment) => !attachment.type.startsWith('image/')) ?? []
  const isGeneratingImage = !!message.isGeneratingImage
  const [isSaving, setIsSaving] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [typedLength, setTypedLength] = useState(message.content.length)
  const previousMessageIdRef = useRef(message.id)

  // Reset loaded state when the image URL changes
  useEffect(() => {
    setImageLoaded(false)
  }, [message.imageUrl])

  useEffect(() => {
    if (previousMessageIdRef.current !== message.id) {
      previousMessageIdRef.current = message.id
      setTypedLength(message.isStreaming ? 0 : message.content.length)
      return
    }

    if (!message.isStreaming) {
      setTypedLength(message.content.length)
      return
    }

    setTypedLength((current) => Math.min(current, message.content.length))
  }, [message.id, message.content.length, message.isStreaming])

  useEffect(() => {
    if (!message.isStreaming) return
    if (!message.content) return
    if (typedLength >= message.content.length) return

    const timeoutId = window.setTimeout(() => {
      setTypedLength((current) => getNextTypedLength(current, message.content.length))
    }, 28)

    return () => window.clearTimeout(timeoutId)
  }, [message.isStreaming, message.content, typedLength])

  const openPreview = useCallback((src: string) => {
    setPreviewSrc(src)
    setPreviewOpen(true)
  }, [])

  const closePreview = useCallback(() => {
    setPreviewOpen(false)
    setPreviewSrc(null)
  }, [])

  const openFilePreview = useCallback((src: string, name: string, mimeType: string) => {
    setFilePreview({ src, name, mimeType })
  }, [])

  const closeFilePreview = useCallback(() => {
    setFilePreview(null)
  }, [])

  const handleSaveAttachmentImage = useCallback(async (e: React.MouseEvent, src: string, name: string) => {
    e.stopPropagation()
    if (isSaving) return
    setIsSaving(true)
    try {
      const response = await fetch(src, { credentials: 'include', referrerPolicy: 'no-referrer' })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name || `image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      window.open(src, '_blank', 'noopener,noreferrer')
    } finally {
      setIsSaving(false)
    }
  }, [isSaving])

  const handleSaveImage = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!message.imageUrl || isSaving) return
    setIsSaving(true)
    try {
      const response = await fetch(message.imageUrl, { credentials: 'include', referrerPolicy: 'no-referrer' })
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `image-${Date.now()}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      window.open(message.imageUrl, '_blank', 'noopener,noreferrer')
    } finally {
      setIsSaving(false)
    }
  }, [message.imageUrl, isSaving])

  // Streaming with no content yet (and no image)
  if (!message.content && !hasImage && !isGeneratingImage && message.isStreaming) {
    return (
      <div className="flex gap-3 py-4">
        <BotAvatar />
        <div className="flex-1 min-w-0 pt-1">
          <StreamingLabel label="Thinking..." />
        </div>
      </div>
    )
  }

  if (!message.content && !hasImage && !isGeneratingImage) return null

  const displayedContent = message.isStreaming
    ? message.content.slice(0, typedLength)
    : message.content

  return (
    <div className="flex gap-3 py-4">
      <BotAvatar />
      {/* Message card — neobrutalism */}
      <div className="flex-1 min-w-0 rounded-base border-2 border-nb-border bg-secondary-bg px-4 py-3 nb-shadow">
        {isError ? (
          <div className="text-base text-destructive font-heading">
            {message.content}
          </div>
        ) : displayedContent ? (
          viewMode === 'rendered' ? (
            <div className="text-base font-base">
              <Markdown id={message.id}>{displayedContent}</Markdown>
            </div>
          ) : (
            <pre className="text-base text-foreground/80 whitespace-pre-wrap font-mono">
              {displayedContent}
            </pre>
          )
        ) : null}

        {imageAttachments.length > 0 && (
          <div
            className={cn(
              'grid w-full grid-cols-2 gap-2',
              message.content && 'mt-3 border-t-2 border-nb-border pt-3',
            )}
          >
            {imageAttachments.map((attachment) => (
              <div key={attachment.id} className="group/img relative overflow-hidden rounded-base border-2 border-nb-border bg-bg">
                <AttachmentImage
                  src={attachment.url}
                  mimeType={attachment.type}
                  alt={attachment.name}
                  className="block aspect-square h-auto w-full cursor-pointer object-cover transition-opacity hover:opacity-90"
                />
                <button
                  type="button"
                  className="absolute inset-0 z-0"
                  onClick={() => openPreview(attachment.url)}
                  aria-label={`Preview ${attachment.name}`}
                />
                <div className="absolute bottom-3 right-3 z-10 opacity-0 transition-opacity group-hover/img:opacity-100">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className={cn(
                          'inline-flex items-center justify-center rounded-base border-2 border-nb-border bg-secondary-bg/90 p-1.5 backdrop-blur-sm transition-colors nb-shadow-sm',
                          isSaving ? 'cursor-not-allowed text-foreground/40' : 'text-foreground/70 hover:bg-main/20',
                        )}
                        onClick={(e) => handleSaveAttachmentImage(e, attachment.url, attachment.name)}
                        disabled={isSaving}
                      >
                        {isSaving ? <Spinner className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Save image</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        )}

        {fileAttachments.length > 0 && (
          <div className={cn('flex flex-wrap gap-1.5', (message.content || imageAttachments.length > 0) && 'mt-3 pt-3 border-t-2 border-nb-border')}>
            {fileAttachments.map((attachment) => (
              <button
                key={attachment.id}
                type="button"
                className="inline-flex items-center gap-1 rounded-base border-2 border-nb-border bg-bg px-2 py-1 text-xs font-base text-foreground transition-colors hover:bg-main hover:text-main-foreground"
                onClick={() => openFilePreview(attachment.url, attachment.name, attachment.type)}
              >
                <FileText className="h-3 w-3" />
                <span className="truncate max-w-[160px]">{attachment.name}</span>
              </button>
            ))}
          </div>
        )}

        {/* Image generation loading */}
        {isGeneratingImage && !hasImage && (
          <div className={cn('py-4', message.content && 'mt-3 pt-3 border-t-2 border-nb-border')}>
            <div className="relative w-full max-w-[512px] aspect-square rounded-base border-2 border-nb-border bg-foreground/5 overflow-hidden flex flex-col items-center justify-center gap-4">
              {/* Shimmer sweep */}
              <div className="absolute inset-0 nb-shimmer" />

              {/* Animated icon */}
              <div className="relative w-14 h-14 rounded-base border-2 border-nb-border bg-main flex items-center justify-center nb-shadow-sm animate-bounce">
                <ImageIcon className="h-7 w-7 text-main-foreground" />
              </div>

              {/* Blinking dots + label */}
              <div className="relative flex flex-col items-center gap-2">
                <div className="flex items-center gap-1.5">
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      className="block h-2 w-2 rounded-full border-2 border-nb-border bg-main"
                      style={{ animation: `nb-blink 1.2s ${i * 0.2}s infinite` }}
                    />
                  ))}
                </div>
                <StreamingLabel label="Generating image..." />
              </div>
            </div>
          </div>
        )}

        {/* Generated image */}
        {hasImage && (
          <div className={cn('group/img relative max-w-[512px]', message.content && 'mt-3 pt-3 border-t-2 border-nb-border')}>
            {/* Placeholder keeps the aspect-ratio while the image loads */}
            {!imageLoaded && (
              <div className="w-full aspect-square rounded-base border-2 border-nb-border bg-foreground/5 flex items-center justify-center animate-pulse">
                <ImageIcon className="h-10 w-10 text-foreground/20" />
              </div>
            )}
            <img
              src={message.imageUrl}
              alt="Generated image"
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
              onLoad={() => setImageLoaded(true)}
              onClick={() => openPreview(message.imageUrl!)}
              className={cn(
                'w-full rounded-base border-2 border-nb-border hover:opacity-90 transition-opacity cursor-pointer',
                !imageLoaded && 'absolute inset-0 invisible',
              )}
            />
            {/* Hover download button */}
            {imageLoaded && (
              <div className="absolute bottom-3 right-3 opacity-0 group-hover/img:opacity-100 transition-opacity">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'inline-flex items-center justify-center rounded-base border-2 border-nb-border bg-secondary-bg/90 backdrop-blur-sm p-1.5 transition-colors nb-shadow-sm',
                        isSaving ? 'text-foreground/40 cursor-not-allowed' : 'text-foreground/70 hover:bg-main/20',
                      )}
                      onClick={handleSaveImage}
                      disabled={isSaving}
                    >
                      {isSaving ? <Spinner className="h-4 w-4" /> : <Download className="h-4 w-4" />}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Save image</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        )}

        {/* Action bar */}
        {!message.isStreaming && !isError && (
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mt-3 pt-2 border-t-2 border-nb-border">
            {/* Timestamp */}
            <span className="text-xs text-foreground/30 font-base mr-auto">
              {formatTime(message.timestamp)}
            </span>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 text-xs font-heading rounded-base border-2 border-transparent px-2 py-1 text-foreground/50 hover:border-nb-border hover:bg-main/10 transition-all"
                  onClick={handleCopy}
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>Copy to clipboard</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 text-xs font-heading rounded-base border-2 border-transparent px-2 py-1 text-foreground/50 hover:border-nb-border hover:bg-main/10 transition-all"
                  onClick={() => setViewMode((m) => m === 'rendered' ? 'markdown' : 'rendered')}
                >
                  <FileText className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{viewMode === 'rendered' ? 'Markdown' : 'Rendered'}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent>Toggle markdown view</TooltipContent>
            </Tooltip>

            {/* Regenerate button */}
            {isLast && onRegenerate && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1.5 text-xs font-heading rounded-base border-2 border-transparent px-2 py-1 text-foreground/50 hover:border-nb-border hover:bg-main/10 transition-all"
                    onClick={onRegenerate}
                  >
                    <RefreshCw className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Regenerate</span>
                  </button>
                </TooltipTrigger>
                <TooltipContent>Regenerate response</TooltipContent>
              </Tooltip>
            )}

            {/* Vote buttons */}
            {onVote && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'inline-flex items-center rounded-base border-2 px-1.5 py-1 text-xs transition-all',
                        message.vote === 'up'
                          ? 'border-success bg-success/15 text-success font-heading'
                          : 'border-transparent text-foreground/40 hover:border-nb-border hover:bg-main/10',
                      )}
                      onClick={handleVoteUp}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Helpful</TooltipContent>
                </Tooltip>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'inline-flex items-center rounded-base border-2 px-1.5 py-1 text-xs transition-all',
                        message.vote === 'down'
                          ? 'border-destructive bg-destructive/15 text-destructive font-heading'
                          : 'border-transparent text-foreground/40 hover:border-nb-border hover:bg-main/10',
                      )}
                      onClick={handleVoteDown}
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Not helpful</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        )}

        {/* Streaming indicator */}
        {message.isStreaming && message.content && (
          <div className="mt-2 pt-2 border-t-2 border-nb-border">
            <StreamingLabel label="Generating..." />
          </div>
        )}
      </div>

      {/* Fullscreen image preview */}
      {previewSrc && (
        <ImagePreview
          src={previewSrc}
          open={previewOpen}
          onClose={closePreview}
        />
      )}
      {filePreview && (
        <FilePreview
          src={filePreview.src}
          fileName={filePreview.name}
          mimeType={filePreview.mimeType}
          open={true}
          onClose={closeFilePreview}
        />
      )}
    </div>
  )
}
